import { Box } from "@mui/material"
import { useWeb3React } from "@web3-react/core"
import { FixedNumber } from "ethers"
import { formatUnits } from "ethers/lib/utils"
import _ from "lodash"
import React from "react"
import { useTranslation } from "react-i18next"
import { useBoolean } from "src/hooks/useBoolean"
import Button from "src/components/Button"
import Text from "src/components/Text"
import { FER, XFER } from "src/constants"
import { BIG_ZERO, BIG_ZERO_FIXED } from "src/constants/bigNumber"
import { useUserFerroBoost } from "src/state/xFer/hooks"
import { formatNumber } from "src/utils/parseStringToBigNumber"
import { useVaultsApr } from "../../hooks/useVaultsApr"
import { useClaimVaultReward } from "src/hooks/useClaimReward"

import PieChartSection from "./PieChartSection"
import Web3Status from "src/components/Web3Status"
import { useIsMobile } from "src/hooks/useIsMobile"
import FerroPriceText from "src/components/FerroPriceText"
import HarvestOptionModal from "src/components/HarvestOptionModal"
import { HarvestMode } from "src/state/xFer/types"

const MyEarnings: React.FC = () => {
  const { t } = useTranslation()

  const { account } = useWeb3React()
  const isMobile = useIsMobile()

  const { boostData } = useUserFerroBoost()
  const { pendingFer, stakes } = boostData
  const { claim } = useClaimVaultReward()
  const [showHarvestModal, openHarvestModal, closeHarvestModal] =
    useBoolean(false)
  const vaultsApr = useVaultsApr()
  const totalLocked = stakes
    .filter((x) => x.active)
    .map((x) => x.amount)
    .reduce((a, b) => a.add(b), BIG_ZERO)

  const totalUnlocked = stakes
    .filter((x) => x.active && x.unlockTimestamp <= new Date().getTime() / 1000)
    .map((x) => x.amount)
    .reduce((a, b) => a.add(b), BIG_ZERO)

  const grouped = _.groupBy(
    boostData.stakes
      .map((x, i) => ({
        ...x,
        stakeId: i,
      }))
      .filter((x) => x.active),
    (p) => p.poolId,
  )

  const data = Object.keys(grouped).map((k) => ({
    pid: k,
    value: grouped[k].reduce((a, b) => a.add(b.amount), BIG_ZERO),
  }))

  const weightedAmount = data
    .map((x) => {
      const apr =
        vaultsApr.find((pool) => pool.poolId === Number(x.pid))?.apr || "0"
      return FixedNumber.from(x.value).mulUnsafe(FixedNumber.from(apr))
    })
    .reduce((a, b) => a.addUnsafe(b), BIG_ZERO_FIXED)

  const totalAmount = data.reduce((a, b) => a.add(b.value), BIG_ZERO)
  const weightedApr = totalAmount.isZero()
    ? BIG_ZERO_FIXED
    : weightedAmount.divUnsafe(FixedNumber.from(totalAmount))

  return (
    <Box>
      <Text variant="h5">{t("My Earnings")}</Text>
      <Box display="flex" justifyContent="space-between" mt={2}>
        <Text>{t("AVG Earnings APR")}</Text>
        <Box>
          {!weightedApr.isZero() ? formatNumber(weightedApr, 2, 2) : "0.00"}%
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt={1}>
        <Text>{t("xFER Staked")}</Text>
        <Text ml="4px">
          {formatNumber(formatUnits(totalLocked, XFER.decimals), 2, 6)}
        </Text>
      </Box>
      <Box mb={3} display="flex" justifyContent="space-between" mt={1}>
        <Text>{t("xFER Unlocked")}</Text>
        <Text ml="4px">
          {formatNumber(formatUnits(totalUnlocked, XFER.decimals), 2, 6)}
        </Text>
      </Box>

      {!account ? (
        <Web3Status fullWidth />
      ) : (
        <Box>
          {totalLocked.gt("0") && <PieChartSection data={data} />}
          <Box bgcolor="#00000080" p={3} borderRadius="10px" mt={3}>
            <Text size="small">{t("Pending Rewards")}</Text>
            <Box
              display={isMobile ? "block" : "flex"}
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Box>
                <Text bold my={0.5}>{`${FER.symbol} ${formatNumber(
                  formatUnits(pendingFer, FER.decimals),
                  2,
                  6,
                )}`}</Text>
                <FerroPriceText amount={pendingFer} />
              </Box>
              <Button
                onClick={openHarvestModal}
                disabled={pendingFer.eq("0")}
                size="small"
                width={isMobile ? "100%" : "auto"}
                mt={isMobile ? 2 : 0}
              >
                {t("Harvest")}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <HarvestOptionModal
        open={showHarvestModal}
        onClose={closeHarvestModal}
        harvest={claim}
        harvestMode={HarvestMode.Vault}
        harvestAmount={pendingFer}
      />
    </Box>
  )
}

export default MyEarnings
